import {Helmet} from '@dr.pogodin/react-helmet';
import {Card, Form, FormField, FormSubmitHandler, Separator} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useCreatePreAccountingProcessingSettingsMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

const breadcrumbs = [
  {
    label: i18n.t('page.accounting.labels.preProcessingCalculation'),
    href: settingsRoutes.preAccountingProcessingOverview,
  },
];

const schema = z.object({
  branchId: z.string().nonempty(),
  notifyAccountingDocuments: z.boolean(),
  notifyStockReceipts: z.boolean(),
  notifyStockIssues: z.boolean(),
  notifyStockTransfers: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export function PreAccountingProcessingNew() {
  const navigate = useNavigate();

  const {branchOptions, isLoading, isError} = useBranches();
  const [createProcessingItem] = useCreatePreAccountingProcessingSettingsMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await createProcessingItem({
      createPreAccountingProcessingSettingsRequestBody: values,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.preAccountingProcessingOverview);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const title = i18n.t('page.accounting.labels.newDefinition');

  return (
    <>
      <Helmet title={title} />

      <SettingsTemplate
        isError={isError}
        isLoading={isLoading}
        header={{
          title,
          breadcrumbs,
        }}
        data-testid={testIds.settings.preAccountingProcessingNew('settings-template')}
      >
        <Form<FormValues>
          onSubmit={handleSubmit}
          experimentalZodSchema={schema}
          defaultValues={{
            notifyStockTransfers: false,
            notifyStockIssues: false,
            notifyStockReceipts: false,
            notifyAccountingDocuments: false,
          }}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <VStack spacing={4}>
              <SettingsSection>
                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="choice"
                    name="branchId"
                    isNotClearable
                    options={branchOptions}
                    label={i18n.t('entity.cashRegister.parameters.branch')}
                    isRequired
                    data-testid={testIds.settings.preAccountingProcessingNew('branch')}
                  />
                </Grid>
                <Separator />

                <VStack spacing={4}>
                  <Card variant="inlineGrey" title={i18n.t('entity.accounting.labels.accounting')}>
                    <FormField
                      control={control}
                      type="checkbox"
                      name="notifyAccountingDocuments"
                      label={i18n.t('entity.accounting.labels.notifyAccountingDocuments')}
                      data-testid={testIds.settings.preAccountingProcessingNew(
                        'notifyAccountingDocuments'
                      )}
                    />
                  </Card>

                  <Card
                    variant="inlineGrey"
                    title={i18n.t('page.accountingSettings.labels.warehouseMovements')}
                  >
                    <VStack spacing={2}>
                      <FormField
                        control={control}
                        type="checkbox"
                        name="notifyStockReceipts"
                        label={i18n.t('page.accountingSettings.labels.notifyReceipt')}
                        data-testid={testIds.settings.preAccountingProcessingNew('notifyReceipt')}
                      />
                      <FormField
                        control={control}
                        type="checkbox"
                        name="notifyStockIssues"
                        label={i18n.t('page.accountingSettings.labels.notifyIssue')}
                        data-testid={testIds.settings.preAccountingProcessingNew('notifyIssue')}
                      />
                      <FormField
                        control={control}
                        type="checkbox"
                        name="notifyStockTransfers"
                        label={i18n.t('page.accountingSettings.labels.notifyTransfer')}
                        data-testid={testIds.settings.preAccountingProcessingNew('notifyTransfer')}
                      />
                    </VStack>
                  </Card>
                </VStack>
              </SettingsSection>

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: i18n.t('general.actions.discardChanges'),
                    onClick: () =>
                      navigateWithoutBlocker(settingsRoutes.preAccountingProcessingOverview),
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: i18n.t('general.actions.saveChanges'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
